"use client"

import { useCallback, useMemo, useRef, useState } from "react"
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from "react"
import {
  ROADMAP_ERAS,
  flattenErasToMilestones,
  assertRoadmapErasValid,
  type RoadmapMilestone,
  type RoadmapMilestoneStatus,
} from "@/lib/dapp-content"

type Milestone = RoadmapMilestone

// Dev-time invariant: fail loudly if a milestone isn't slotted into an era.
if (process.env.NODE_ENV !== "production") assertRoadmapErasValid()

const STATUS_STYLE: Record<
  RoadmapMilestoneStatus,
  { solid: string; soft: string; glow: string; label: string }
> = {
  completed: { solid: "#8fbfa8", soft: "rgba(143,191,168,0.12)", glow: "rgba(143,191,168,0.45)", label: "DELIVERED" },
  locked: { solid: "#00e5ff", soft: "rgba(0,229,255,0.12)", glow: "rgba(0,229,255,0.45)", label: "SCHEDULED" },
  future: { solid: "#c9a96a", soft: "rgba(201,169,106,0.12)", glow: "rgba(201,169,106,0.40)", label: "FUTURE" },
}

function defaultMilestoneIndex(milestones: readonly Milestone[]) {
  const scheduled = milestones.findIndex((m) => m.status === "locked")
  if (scheduled >= 0) return scheduled
  const lastDone = milestones.findLastIndex((m) => m.status === "completed")
  if (lastDone >= 0 && lastDone < milestones.length - 1) return lastDone + 1
  return 0
}

export function RoadmapView() {
  const milestones = useMemo(() => flattenErasToMilestones(), [])
  // phase -> flat index, so grouped render can recover the correct nav index
  const flatIndex = useMemo(() => new Map(milestones.map((m, i) => [m.phase, i])), [milestones])

  const [selected, setSelected] = useState(() => defaultMilestoneIndex(milestones))
  const [hovered, setHovered] = useState<number | null>(null)
  const active = hovered ?? selected
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([])

  const m = milestones[active]!
  const sc = STATUS_STYLE[m.status].solid
  const completed = milestones.filter((x) => x.status === "completed").length
  const progressPct = Math.round((completed / milestones.length) * 100)
  const depthStep = Math.floor(1000 / Math.max(1, milestones.length - 1))

  const focusTo = useCallback((i: number) => {
    setSelected(i)
    setHovered(null)
    btnRefs.current[i]?.focus()
  }, [])

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault()
      focusTo(Math.min(milestones.length - 1, selected + 1))
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault()
      focusTo(Math.max(0, selected - 1))
    } else if (e.key === "Home") {
      e.preventDefault()
      focusTo(0)
    } else if (e.key === "End") {
      e.preventDefault()
      focusTo(milestones.length - 1)
    }
  }

  return (
    <div className="dc-wrap">
      <div className="dc-head">
        <h2>
          Roadmap <span className="dim">/</span> <span className="accent">Era_Map_</span>
        </h2>
        <div
          className="dc-progress"
          role="img"
          aria-label={`${completed} of ${milestones.length} milestones delivered`}
        >
          <div className="dc-progress-meta">
            <span>
              {completed} / {milestones.length} DELIVERED
            </span>
            <span className="dc-progress-pct">{progressPct}%</span>
          </div>
          <div className="dc-progress-track">
            <div className="dc-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      </div>

      <div className="dc-body">
        <div
          className="dc-eragrid"
          role="group"
          aria-label="Roadmap milestones grouped by era — use arrow keys to navigate"
          onKeyDown={onKeyDown}
          onMouseLeave={() => setHovered(null)}
        >
          {ROADMAP_ERAS.map((era) => {
            const eraMs = era.phases.map((p) => milestones[flatIndex.get(p)!]!)
            const done = eraMs.filter((x) => x.status === "completed").length
            return (
              <div className="dc-era" key={era.id}>
                <div className="dc-era-head">
                  <span className="dc-era-label">{era.label}</span>
                  <span className="dc-era-count" aria-label={`${done} of ${eraMs.length} delivered`}>
                    {done}/{eraMs.length}
                  </span>
                </div>
                <div className="dc-era-chips">
                  {era.phases.map((p) => {
                    const i = flatIndex.get(p)!
                    const ms = milestones[i]!
                    const s = STATUS_STYLE[ms.status]
                    const isActive = i === active
                    const isTGE = "isTgeFault" in ms && ms.isTgeFault === true
                    return (
                      <button
                        key={ms.phase}
                        ref={(el) => {
                          btnRefs.current[i] = el
                        }}
                        type="button"
                        aria-pressed={isActive}
                        aria-label={`${ms.phase} ${ms.title}, ${ms.dateShort}, ${s.label}`}
                        tabIndex={i === selected ? 0 : -1}
                        className={`dc-chip dc-chip--${ms.status}${isActive ? " is-active" : ""}${
                          isTGE ? " is-tge" : ""
                        }`}
                        style={
                          {
                            "--sc": s.solid,
                            "--sc-soft": s.soft,
                            "--sc-glow": s.glow,
                          } as CSSProperties
                        }
                        onMouseEnter={() => setHovered(i)}
                        onClick={() => {
                          setSelected(i)
                          setHovered(null)
                        }}
                        onFocus={() => {
                          setSelected(i)
                          setHovered(null)
                        }}
                      >
                        <span className="dc-chip-dot" />
                        <span className="dc-chip-phase">{ms.phase}</span>
                        <span className="dc-chip-title">{ms.title}</span>
                        <span className="dc-chip-date">{ms.dateShort}</span>
                        {isTGE && <span className="dc-chip-fault">⚡</span>}
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        <div className="dc-side">
          <div className="dc-active-detail">
            <div className="crumb">
              <span style={{ color: sc }}>// {m.phase}</span>
              <span style={{ color: "var(--text-faint)" }}>{m.strata}</span>
            </div>
            <div className="stratum-num" style={{ color: sc }}>
              {String(active + 1).padStart(2, "0")}
            </div>
            <h3>{m.title}</h3>
            <div className="date">
              {m.dateShort} — {m.date}
            </div>
            <p>{m.detail}</p>
            <div className="rows">
              <div className="row">
                <span>STATUS</span>
                <span
                  className={`v ${m.status === "completed" ? "ok" : m.status === "locked" ? "cy" : ""}`}
                >
                  {m.status.toUpperCase()}
                </span>
              </div>
              <div className="row">
                <span>SIG</span>
                <span className="v">{m.sig}</span>
              </div>
              <div className="row">
                <span>STRATUM</span>
                <span className="v">{m.strata}</span>
              </div>
              <div className="row">
                <span>DEPTH</span>
                <span className="v">{(active * depthStep).toString().padStart(4, "0")}m</span>
              </div>
            </div>
          </div>
          <div className="legend-block">
            <p className="legend-block-kicker">// READING_GUIDE</p>
            <p className="legend-block-lede">
              Milestones grouped by era. Click or use ↑↓ to inspect a phase.
            </p>
            <p className="legend-block-keys">
              <span className="legend-key legend-key--done">Delivered</span> shipped ·{" "}
              <span className="legend-key legend-key--solid">Scheduled</span> target window ·{" "}
              <span className="legend-key legend-key--cross">Future</span> planned
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
